import { APITypes, MedicalDataTypes } from '../../../../../primary-record-types';
import { PrimaryRecordResource } from '../../../../../primary-record-types/MedicalDataTypes';

export const getEditDefaultResource = <TResourceType extends MedicalDataTypes.PrimaryRecordResource>(
    listItem: APITypes.medicalDataService.ListResourceResponseItem<PrimaryRecordResource>,
    resourceType: TResourceType['type'],
): TResourceType => {
    if (listItem.type !== resourceType) {
        throw new Error('Resource type mismatch');
    }

    if (listItem.type === 'condition') {
        const returnValue: MedicalDataTypes.PrimaryRecordCondition = {
            version: listItem.version,
            id: listItem.id,
            type: 'condition',
            name: listItem.name,
            startDateTime: listItem.startDateTime,
            endDateTime: listItem.endDateTime,
            status: listItem.status,
            notes: listItem.notes,
        };

        return returnValue as TResourceType;
    } else if (listItem.type === 'allergy') {
        const returnValue: MedicalDataTypes.PrimaryRecordAllergy = {
            version: listItem.version,
            id: listItem.id,
            type: 'allergy',
            name: listItem.name,
            status: listItem.status,
            notes: listItem.notes,
            allergyType: listItem.allergyType,
            reactionType: listItem.reactionType,
        };

        return returnValue as TResourceType;
    } else if (listItem.type === 'medication') {
        const returnValue: MedicalDataTypes.PrimaryRecordMedication = {
            version: listItem.version,
            id: listItem.id,
            type: 'medication',
            name: listItem.name,
            fdaName: listItem.fdaName,
            genericNameOrActiveIngredient: listItem.genericNameOrActiveIngredient,
            strength: listItem.strength,
            form: listItem.form,
            route: listItem.route,
            quantity: listItem.quantity,
            frequency: listItem.frequency,
            isActive: listItem.isActive,
            whenToTake: listItem.whenToTake,
            numberOfRefills: listItem.numberOfRefills,
            lastRefillDateTime: listItem.lastRefillDateTime,
            providerCareTeamMemberId: listItem.providerCareTeamMemberId,
            providerName: listItem.providerName,
            startDateTime: listItem.startDateTime,
            endDateTime: listItem.endDateTime,
            notes: listItem.notes,
            asNeeded: listItem.asNeeded,
        };

        return returnValue as TResourceType;
    } else if (listItem.type === 'visit') {
        const returnValue: MedicalDataTypes.PrimaryRecordVisit = {
            version: listItem.version,
            id: listItem.id,
            type: 'visit',
            visitType: listItem.visitType,
            visitTypeOtherLabel: listItem.visitTypeOtherLabel,
            reason: listItem.reason,
            startDateTime: listItem.startDateTime,
            duration: listItem.duration,
            endDateTime: listItem.endDateTime,
            notes: listItem.notes,
            providerCareTeamMemberId: listItem.providerCareTeamMemberId,
            providerName: listItem.providerName,
            address1: listItem.address1,
            address2: listItem.address2,
            city: listItem.city,
            state: listItem.state,
            zipCode: listItem.zipCode,
        };

        return returnValue as TResourceType;
    } else if (listItem.type === 'imaging') {
        const returnValue: MedicalDataTypes.PrimaryRecordImaging = {
            version: listItem.version,
            id: listItem.id,
            type: 'imaging',
            name: listItem.name,
            dateTime: listItem.dateTime,
            notes: listItem.notes,
            results: listItem.results,
            address1: listItem.address1,
            address2: listItem.address2,
            city: listItem.city,
            state: listItem.state,
            zipCode: listItem.zipCode,
        };

        return returnValue as TResourceType;
    } else if (listItem.type === 'procedure') {
        const returnValue: MedicalDataTypes.PrimaryRecordProcedure = {
            version: listItem.version,
            id: listItem.id,
            type: 'procedure',
            name: listItem.name,
            dateTime: listItem.dateTime,
            notes: listItem.notes,
            address1: listItem.address1,
            address2: listItem.address2,
            city: listItem.city,
            state: listItem.state,
            zipCode: listItem.zipCode,
            providerCareTeamMemberId: listItem.providerCareTeamMemberId,
            providerName: listItem.providerName,
            results: listItem.results,
        };

        return returnValue as TResourceType;
    } else if (listItem.type === 'vaccination') {
        const returnValue: MedicalDataTypes.PrimaryRecordVaccination = {
            version: listItem.version,
            id: listItem.id,
            type: 'vaccination',
            vaccineType: listItem.vaccineType,
            dateTime: listItem.dateTime,
            notes: listItem.notes,
            address1: listItem.address1,
            address2: listItem.address2,
            city: listItem.city,
            state: listItem.state,
            zipCode: listItem.zipCode,
        };

        return returnValue as TResourceType;
    } else {
        throw new Error('Resource type not supported');
    }
};
